import React from 'react';
import {
  Beaker, Rocket, CheckCircle2, ArrowRight, Code2, Cpu, Gauge, Shield,
  Lock, Layers, Zap, Database, GraduationCap
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const DevelopmentStory = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [phase1Ref, phase1Visible] = useScrollAnimation({ threshold: 0.2 });
  const [phase2Ref, phase2Visible] = useScrollAnimation({ threshold: 0.2 });
  const [codonRef, codonVisible] = useScrollAnimation({ threshold: 0.2 });
  const [proprietaryRef, proprietaryVisible] = useScrollAnimation({ threshold: 0.2 });
  const [methodRef, methodVisible] = useScrollAnimation({ threshold: 0.2 });

  const proprietaryTech = [
    {
      icon: Zap,
      title: "ASTRA Hot Path Engine™",
      description: "Zero-allocation decision core with proprietary compiler optimizations developed through 11 months of R&D",
      color: "cyan"
    },
    {
      icon: Database,
      title: "Async Cache System™",
      description: "Lock-free indicator cache with hardware-aware memory synchronization — our proprietary approach to data consistency",
      color: "purple"
    },
    {
      icon: Layers,
      title: "Proprietary Ring Buffer",
      description: "Lock-free architecture with proprietary optimizations achieving 950M+ decisions/sec throughput",
      color: "blue"
    },
    {
      icon: Lock,
      title: "ASTRA Native Compilation",
      description: "Proprietary compilation pipeline built on LLVM — the specific optimizations are our trade secret",
      color: "green"
    }
  ];

  const colorClasses = {
    cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400" },
    purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400" },
    blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400" },
    green: { bg: "bg-green-500/10", border: "border-green-500/20", text: "text-green-400" }
  };

  return (
    <section id="methodology" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <Code2 className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Proprietary Architecture</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            How We Achieved <span className="text-gradient">950M+</span> Decisions/sec
          </h2>
          <p className="text-lg text-muted-foreground">
            11 months of development. 15,000+ lines of production code.
            A two-phase methodology that proves correctness before optimizing for speed.
          </p>
        </div>

        {/* Two Phase Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Phase 1 - Python Validation */}
          <div
            ref={phase1Ref}
            className={`transition-all duration-700 ${
              phase1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative h-full p-8 rounded-2xl glass-card hover-glow group">
              {/* Phase Badge */}
              <div className="absolute -top-3 left-8">
                <span className="px-4 py-1.5 text-xs font-bold bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full shadow-lg">
                  PHASE 1: VALIDATION
                </span>
              </div>

              <div className="pt-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Beaker className="w-7 h-7 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Python Proof of Concept</h3>
                    <p className="text-sm text-blue-400">Architecture Validation</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Achieved 1.1-1.5 microseconds</span>
                      <p className="text-sm text-muted-foreground">Industry said Python couldn't do HFT</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Validated all 6 protocols</span>
                      <p className="text-sm text-muted-foreground">FIX, SBE, ITCH, OUCH, FAST, WebSocket</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Full compliance framework</span>
                      <p className="text-sm text-muted-foreground">MiFID II, risk controls, audit trails</p>
                    </div>
                  </div>
                </div>

                {/* Result Box */}
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm text-blue-300">
                    <span className="font-semibold">Result:</span> Proved the architecture works.
                    Every feature validated before optimization began.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 - Codon Optimization */}
          <div
            ref={phase2Ref}
            className={`transition-all duration-700 delay-100 ${
              phase2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative h-full p-8 rounded-2xl glass-intense border-2 border-cyan-500/30 glow-cyan-soft group">
              {/* Phase Badge */}
              <div className="absolute -top-3 left-8">
                <span className="px-4 py-1.5 text-xs font-bold bg-gradient-to-r from-cyan-500 to-green-500 text-white rounded-full shadow-lg">
                  PHASE 2: OPTIMIZATION
                </span>
              </div>

              <div className="pt-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Rocket className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Native Compilation</h3>
                    <p className="text-sm text-cyan-400">Performance Breakthrough</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Achieved ~1-2ns amortized</span>
                      <p className="text-sm text-muted-foreground">~1000x faster than Phase 1</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Native machine code compilation</span>
                      <p className="text-sm text-muted-foreground">LLVM-optimized, zero interpreter overhead</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-white font-medium">Cross-platform verified</span>
                      <p className="text-sm text-muted-foreground">ARM64 (~1ns) and x86 (~2ns) amortized</p>
                    </div>
                  </div>
                </div>

                {/* Result Box */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-green-500/10 border border-cyan-500/20">
                  <p className="text-sm text-cyan-300">
                    <span className="font-semibold">Result:</span> World-class throughput with zero feature loss.
                    950M+ decisions/sec on commodity hardware.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Comparison Arrow */}
        <div className="flex justify-center mb-16">
          <div className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 border border-white/10">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">1.5 µs</div>
              <div className="text-xs text-muted-foreground">Phase 1</div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight className="w-6 h-6 text-purple-400" />
              <span className="text-sm font-bold text-purple-400">~1000x</span>
              <ArrowRight className="w-6 h-6 text-purple-400" />
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">~2 ns</div>
              <div className="text-xs text-muted-foreground">Phase 2 (amortized)</div>
            </div>
          </div>
        </div>

        {/* Why Codon Section */}
        <div
          ref={codonRef}
          className={`mb-16 transition-all duration-700 ${
            codonVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-5xl mx-auto p-8 rounded-2xl glass-intense border border-green-500/20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-4">
                <GraduationCap className="w-4 h-4 text-green-400" />
                <span className="text-sm font-bold text-green-400">WHY CODON</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                MIT's Breakthrough Compiler Technology
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Codon is a high-performance Python compiler developed at MIT. It compiles Python syntax
                directly to native machine code via LLVM, achieving C/C++ performance while maintaining
                Python's elegant syntax and rapid development speed.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-white">Feature</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-yellow-400">Python</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-blue-400">C++</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-green-400">Codon</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-white/5">
                    <td className="px-4 py-3 text-muted-foreground">Syntax</td>
                    <td className="px-4 py-3 text-center text-yellow-400">Simple</td>
                    <td className="px-4 py-3 text-center text-blue-400">Complex</td>
                    <td className="px-4 py-3 text-center text-green-400">Simple (Python)</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="px-4 py-3 text-muted-foreground">Execution Speed</td>
                    <td className="px-4 py-3 text-center text-yellow-400">Slow (~100x)</td>
                    <td className="px-4 py-3 text-center text-blue-400">Fast (1x)</td>
                    <td className="px-4 py-3 text-center text-green-400">Fast (1x)</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="px-4 py-3 text-muted-foreground">Compilation</td>
                    <td className="px-4 py-3 text-center text-yellow-400">Interpreted</td>
                    <td className="px-4 py-3 text-center text-blue-400">Native</td>
                    <td className="px-4 py-3 text-center text-green-400">Native (LLVM)</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="px-4 py-3 text-muted-foreground">GIL Limitation</td>
                    <td className="px-4 py-3 text-center text-yellow-400">Yes</td>
                    <td className="px-4 py-3 text-center text-blue-400">No</td>
                    <td className="px-4 py-3 text-center text-green-400">No</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Dev Speed</td>
                    <td className="px-4 py-3 text-center text-yellow-400">Fast</td>
                    <td className="px-4 py-3 text-center text-blue-400">Slow</td>
                    <td className="px-4 py-3 text-center text-green-400">Fast</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-center">
                <span className="text-green-400 font-medium">The best of both worlds:</span>
                <span className="text-muted-foreground"> Codon lets ASTRA maintain Python's rapid development cycle
                while achieving C++ performance. This is why we can run sophisticated quant strategies at near-FPGA speeds.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Proprietary Technology Section */}
        <div
          ref={proprietaryRef}
          className={`mb-16 transition-all duration-700 ${
            proprietaryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-5xl mx-auto p-8 rounded-2xl glass-intense border border-white/10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-4">
                <Lock className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-bold text-purple-400">PROPRIETARY TECHNOLOGY</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Built on Open Standards. Optimized Beyond Recognition.
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We started with industry-proven foundations, then invested 11 months of
                dedicated R&D to develop proprietary optimizations. The result: 15,000+ lines of trade-secret
                implementation achieving 950M+ decisions/sec on commodity hardware.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {proprietaryTech.map((tech, index) => {
                const colors = colorClasses[tech.color];
                return (
                  <div
                    key={index}
                    className={`p-5 rounded-xl ${colors.bg} border ${colors.border}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center flex-shrink-0`}>
                        <tech.icon className={`w-5 h-5 ${colors.text}`} />
                      </div>
                      <div>
                        <h4 className={`font-semibold ${colors.text} mb-1`}>{tech.title}</h4>
                        <p className="text-sm text-muted-foreground">{tech.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm text-center">
                <span className="text-yellow-400 font-medium">Knowing the ingredients doesn't mean you can bake the cake.</span>
                <span className="text-muted-foreground"> The value is in 11 months of R&D, 15,000+ lines of proprietary code,
                and the complete integration of decision engine, OMS, risk, compliance, and 6 protocols — battle-tested and production-ready.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Methodology Section */}
        <div
          ref={methodRef}
          className={`transition-all duration-700 ${
            methodVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Why This Approach Works
            </h3>

            <div className="grid sm:grid-cols-3 gap-6">
              {/* Step 1 */}
              <div className="text-center p-6 rounded-2xl glass-card">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <Beaker className="w-6 h-6 text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">1</div>
                <h4 className="font-semibold text-white mb-2">Validate First</h4>
                <p className="text-sm text-muted-foreground">
                  Prove the architecture works correctly before optimizing for speed
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center p-6 rounded-2xl glass-card">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Gauge className="w-6 h-6 text-purple-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">2</div>
                <h4 className="font-semibold text-white mb-2">Optimize Second</h4>
                <p className="text-sm text-muted-foreground">
                  Push to world-class nanosecond performance with proven foundations
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center p-6 rounded-2xl glass-card">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">3</div>
                <h4 className="font-semibold text-white mb-2">Zero Compromise</h4>
                <p className="text-sm text-muted-foreground">
                  Every feature from Phase 1 works in Phase 2. Speed without sacrifice
                </p>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 border border-white/10">
                <div className="flex items-center gap-3">
                  <Cpu className="w-6 h-6 text-cyan-400" />
                  <span className="text-white font-medium">Verify our claims on your hardware:</span>
                </div>
                <code className="px-4 py-2 rounded-lg bg-black/50 font-mono text-sm text-cyan-400 border border-cyan-500/20">
                  ./astra --benchmark
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DevelopmentStory;
